import { FormEvent, ReactNode, useEffect, useState } from 'react';
import { backendApi, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import PlanCardPreview, { DEFAULT_ACCENT, FeatureItem } from '../components/PlanCardPreview';

/** What the slider range is when none is set (matches the customer-facing defaults). */
const DEFAULT_MIN_SITES = 1;
const DEFAULT_MAX_SITES = 100;
/** Stripe allows 10 tiers on one price: the base band plus this many discount bands, with room to spare. */
const MAX_BANDS = 8;

/** Mirrors publicPlanCard() in wpcursor-backend/src/services/planCardService.js */
export interface Plan {
  id: string;
  key: string;
  name: string;
  tagline: string | null;
  priceCents: number;
  compareAtCents: number | null;
  interval: 'month' | 'year';
  perSite: boolean;
  minSites: number | null;
  maxSites: number | null;
  /** Where the customer's site slider opens; null = the built-in default. */
  defaultSites?: number | null;
  credits: number;
  accentColor: string | null;
  ctaLabel: string | null;
  checkoutPlan: string | null;
  /** Agency only: true once a price has been saved and a Stripe price exists for it, i.e. what is shown is what is billed. */
  chargePortalPrice?: boolean;
  /** Agency only: volume discounts, the per-site price from that many sites up. [] = one flat price. */
  priceTiers?: { from: number; priceCents: number }[];
  features: FeatureItem[];
  enabled: boolean;
  sortOrder: number;
}

const API = '/license/admin/plan-cards';
/** The Current Plan card every customer sees: always shown, only its wording is editable. */
export const CURRENT_PLAN_KEY = 'free';
/** Dollars with thousands separators: $30,000, $1,250.50. */
const usd = (cents: number) => {
  const v = cents / 100;
  return `$${v.toLocaleString('en-US', { minimumFractionDigits: v % 1 === 0 ? 0 : 2, maximumFractionDigits: 2 })}`;
};
const optInt = (v: string): number | null => (v.trim() === '' ? null : Number(v));

/** The "Price shown" column: what customers actually see for this plan. */
function priceCell(p: Plan): string {
  const per = p.interval === 'year' ? '/yr' : '/mo';
  // A plan that starts a checkout is billed by Stripe: customers see the Stripe price, unless this is the Agency
  // plan with a price saved here. Anything else would list a number nobody pays.
  if (p.checkoutPlan) return p.key === 'agency' && p.chargePortalPrice ? `${usd(p.priceCents)}/site/mo` : "Stripe's price";
  return `${usd(p.priceCents)}${p.perSite ? '/site' : ''}${per}`;
}

/** A price as dollars and at most two decimals ("19.99"). A comma, an exponent or stray text is not one. */
const PRICE_RE = /^\d+(\.\d{1,2})?$/;
const INT_RE = /^\d+$/;

export default function Plans() {
  const { can } = useAuth();
  const canWrite = can('coupons', 'write');
  const [plans, setPlans] = useState<Plan[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Plan | 'new' | null>(null);

  async function load() {
    setLoading(true);
    try {
      const data = await backendApi.get<{ plans: Plan[] }>(API);
      setPlans(data.plans);
      setError('');
    } catch (err: any) {
      setError((err as ApiError).isForbidden ? 'You do not have access to plans.' : err.message);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { void load(); }, []);

  async function setEnabled(p: Plan, enabled: boolean) {
    setError('');
    setPlans((cur) => cur.map((x) => (x.key === p.key ? { ...x, enabled } : x)));
    // An editor open on this plan follows the switch, so saving it cannot undo what was just switched.
    setEditing((cur) => (cur !== null && cur !== 'new' && cur.key === p.key ? { ...cur, enabled } : cur));
    try {
      await backendApi.patch(`${API}/${p.key}`, { enabled });
    } catch (err: any) {
      setError(err.body?.error || err.message);
      // The list reloads, but an open editor keeps its own copy: put the switch back there too, or saving it
      // would write the value the server just refused.
      setEditing((cur) => (cur !== null && cur !== 'new' && cur.key === p.key ? { ...cur, enabled: p.enabled } : cur));
      await load();
    }
  }

  async function remove(p: Plan) {
    if (!confirm(`Delete the ${p.name} plan card? It disappears from the My Plan page.`)) return;
    setError('');
    try {
      await backendApi.delete(`${API}/${p.key}`);
      // Do not leave an editor open on a plan that no longer exists: saving it would only say "not found".
      setEditing((cur) => (cur !== null && cur !== 'new' && cur.key === p.key ? null : cur));
      await load();
    } catch (err: any) { setError(err.body?.error || err.message); }
  }

  async function reset(p: Plan) {
    if (!confirm(`Reset the ${p.name} plan to the original plan? The price and volume discounts set here are removed, so customers are billed the original Stripe price again, and the name, site limits and features go back to their originals. Current subscribers are not changed.`)) return;
    setError('');
    try {
      await backendApi.post(`${API}/${p.key}/reset`, {});
      // An open editor would still hold the old values: close it rather than let it save them back.
      setEditing((cur) => (cur !== null && cur !== 'new' && cur.key === p.key ? null : cur));
      await load();
    } catch (err: any) { setError(err.body?.error || err.message); }
  }

  return (
    <>
      <header className="page-header">
        <div>
          <h1>Plans</h1>
          <p className="muted">
            The plan cards customers see on their My Plan page. Set each plan's price, site limits and features
            with a live preview, and switch a plan on or off.
          </p>
        </div>
        <div className="row-actions">
          {canWrite && (
            <button className="btn btn-primary" onClick={() => setEditing(editing === 'new' ? null : 'new')}>
              {editing === 'new' ? 'Cancel' : 'New plan'}
            </button>
          )}
        </div>
      </header>

      {error && (
        <div className="alert alert-error alert-dismiss">
          <span>{error}</span>
          <button className="alert-close" aria-label="Dismiss" onClick={() => setError('')}>×</button>
        </div>
      )}

      {editing && (
        <PlanEditor
          key={editing === 'new' ? 'new' : editing.key}
          plan={editing === 'new' ? null : editing}
          onCancel={() => setEditing(null)}
          onSaved={() => { setEditing(null); void load(); }}
        />
      )}

      <div className="card">
        {loading ? (
          <p className="muted">Loading plans…</p>
        ) : plans.length === 0 ? (
          <p className="muted">No plans yet.</p>
        ) : (
          <table className="table">
            <thead>
              <tr><th>Plan</th><th>Price shown</th><th>Colour</th><th>Buy button</th><th>Shown to customers</th><th /></tr>
            </thead>
            <tbody>
              {plans.map((p) => (
                <tr key={p.id} className={p.enabled ? '' : 'row-muted'}>
                  <td><strong>{p.name}</strong> <span className="mono muted">{p.key}</span></td>
                  <td data-testid={`price-${p.key}`}>{priceCell(p)}</td>
                  <td>
                    <span style={{ display: 'inline-block', width: 14, height: 14, borderRadius: 4, verticalAlign: 'middle', background: p.accentColor ?? DEFAULT_ACCENT }} />
                    {' '}<span className="mono muted">{p.accentColor ?? DEFAULT_ACCENT}</span>
                  </td>
                  <td>{p.key === CURRENT_PLAN_KEY ? 'Current Plan card' : p.checkoutPlan ? 'Checkout' : 'Contact us'}</td>
                  <td>
                    {p.key === CURRENT_PLAN_KEY ? (
                      <span className="pill pill-ok">always shown</span>
                    ) : (
                    <label className="switch">
                      <input type="checkbox" role="switch" aria-label={`Enable ${p.name}`} checked={p.enabled} disabled={!canWrite}
                        onChange={(e) => void setEnabled(p, e.target.checked)} />
                      <span className="switch-track" />
                      <span className={'pill ' + (p.enabled ? 'pill-ok' : 'pill-off')}>{p.enabled ? 'enabled' : 'disabled'}</span>
                    </label>
                    )}
                  </td>
                  <td className="right">
                    {canWrite && (
                      <>
                        {p.key === 'agency' && (
                          <>
                            <button className="btn btn-sm" onClick={() => reset(p)}>Reset plan</button>
                            {' '}
                          </>
                        )}
                        <button className="btn btn-sm" onClick={() => setEditing(p)}>Edit</button>
                        {' '}
                        {p.key !== CURRENT_PLAN_KEY && p.key !== 'agency' && (
                          <button className="btn btn-sm btn-danger" onClick={() => remove(p)}>Delete</button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

/** One titled group of related settings, with a sentence on what it does. */
function Section({ title, description, children, testId }: { title: string; description?: string; children: ReactNode; testId?: string }) {
  return (
    <section className="plan-section" data-testid={testId}>
      <h3 className="plan-section-title">{title}</h3>
      {description && <p className="plan-section-desc">{description}</p>}
      {children}
    </section>
  );
}

/**
 * What is wrong with the volume-discount rows, in words, or ''. The backend checks the same rules; saying so here
 * keeps the Save button honest instead of waiting for a refusal.
 */
function bandsProblem(bands: { from: string; price: string }[], base: number | null, max: number | null): string {
  if (base == null) return 'Enter the base price per site first: volume discounts are measured from it.';
  let prevFrom = 1;
  let prevPrice = base;
  for (let i = 0; i < bands.length; i++) {
    const n = i + 1;
    const from = bands[i].from.trim();
    const price = bands[i].price.trim();
    if (!INT_RE.test(from) || Number(from) < 2) return `Discount ${n}: start at 2 sites or more, as a whole number.`;
    if (Number(from) <= prevFrom) return `Discount ${n}: start at a higher number of sites than the one before.`;
    if (max != null && Number(from) > max) return `Discount ${n}: starts above the maximum number of sites (${max}).`;
    if (!PRICE_RE.test(price) || Number(price) <= 0) return `Discount ${n}: enter a price per site above $0.`;
    if (Number(price) >= prevPrice) return `Discount ${n}: the price must be lower than the one before it ($${prevPrice}).`;
    prevFrom = Number(from);
    prevPrice = Number(price);
  }
  return '';
}

function PlanEditor({ plan, onCancel, onSaved }: { plan: Plan | null; onCancel: () => void; onSaved: () => void }) {
  const isEdit = plan !== null;
  const isCurrent = plan?.key === CURRENT_PLAN_KEY;
  /* The plan keyed `agency` is the one customers buy. Its price is billed through Stripe: saving a price
     creates the Stripe price for it. Until a price has been saved nothing is billed from here and customers
     keep paying the original Stripe price, so the field starts empty rather than showing a number that
     is not what anyone pays. */
  const isAgencyCard = plan?.key === 'agency';
  const billed = !!plan?.chargePortalPrice;
  const startsCheckout = !!plan?.checkoutPlan;
  /* Standard and Team are priced by their Stripe price, not here. So is any other plan that starts Agency checkout:
     the customer's card shows Agency's price for it, whatever is typed. Only the plan keyed `agency` has a price here. */
  const stripeSetsPrice = plan?.checkoutPlan === 'standard' || plan?.checkoutPlan === 'team' || (plan?.checkoutPlan === 'agency' && !isAgencyCard);

  const [f, setF] = useState({
    key: plan?.key ?? '',
    name: plan?.name ?? '',
    price: plan && (billed || !isAgencyCard) ? String(plan.priceCents / 100) : '',
    minSites: plan?.minSites == null ? '' : String(plan.minSites),
    maxSites: plan?.maxSites == null ? '' : String(plan.maxSites),
    credits: plan ? String(plan.credits) : '',
    tagline: plan?.tagline ?? '',
    enabled: plan?.enabled ?? false,
  });
  const [items, setItems] = useState<FeatureItem[]>(plan?.features ?? []);
  /* Volume discounts (Agency): "from N sites, $X a site". Text fields for the same reason as the price. */
  const [bands, setBands] = useState<{ from: string; price: string }[]>(
    (plan?.priceTiers ?? []).map((t) => ({ from: String(t.from), price: String(t.priceCents / 100) })),
  );
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  // The list's switch changes `plan.enabled` while this editor is open; follow it (see setEnabled above).
  useEffect(() => {
    if (plan) setF((cur) => (cur.enabled === plan.enabled ? cur : { ...cur, enabled: plan.enabled }));
  }, [plan?.enabled]); // eslint-disable-line react-hooks/exhaustive-deps

  const patchItem = (i: number, part: Partial<FeatureItem>) =>
    setItems((cur) => cur.map((it, j) => (j === i ? { ...it, ...part } : it)));
  const cents = (v: string) => Math.round(Number(v || 0) * 100);

  /* The price is a text field, not a number field: a browser reports an unparseable number ("19,99", "1e") as an
     EMPTY value, which would read as "no price" and let the save through without the price the admin thought they set. */
  const priceTyped = f.price.trim();
  const priceBlank = priceTyped === '';
  const priceValid = PRICE_RE.test(priceTyped) && Number(priceTyped) > 0;
  const priceCents = priceValid ? cents(priceTyped) : 0;
  const limitsValid = [f.minSites, f.maxSites].every((v) => v.trim() === '' || (INT_RE.test(v.trim()) && Number(v) >= 1));
  const min = limitsValid ? optInt(f.minSites) : null;
  const max = limitsValid ? optInt(f.maxSites) : null;
  const bandProblem = !isAgencyCard || bands.length === 0 ? '' : bandsProblem(bands, priceValid ? Number(priceTyped) : null, max);
  const problem =
    bandProblem ? bandProblem
    : !isCurrent && !stripeSetsPrice && !priceBlank && !priceValid ? 'Enter a price per site above $0, like 19.99.'
      : !isCurrent && isAgencyCard && billed && priceBlank ? 'Enter a price per site. It is what customers are billed.'
        : !isCurrent && !isEdit && !stripeSetsPrice && priceBlank ? 'Enter a price per site.'
          : !limitsValid ? 'The site limits must be whole numbers of at least 1.'
            : min != null && max != null && min > max ? 'The minimum number of sites cannot be above the maximum.'
              : '';
  // What the customer's card shows: the price Stripe bills, unless a price has been entered for Agency.
  const stripePriced = (isAgencyCard && priceBlank) || stripeSetsPrice;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (problem) { setError(problem); return; }
    setError(''); setBusy(true);
    const featureList = items
      .filter((it) => it.name.trim())
      .map((it) => ({
        name: it.name.trim(),
        ...(it.info?.trim() ? { info: it.info.trim() } : {}),
        ...(it.badge?.trim() ? { badge: it.badge.trim() } : {}),
        ...(it.comingSoon ? { comingSoon: true } : {}),
      }));
    let body: Record<string, unknown>;
    if (isCurrent) {
      body = { tagline: f.tagline.trim() || null };
    } else {
      /* Only what this form edits is sent, so everything else about an existing plan (colour, button, tagline,
         credits, order…) is left exactly as it is. A price is sent only when there is one: leaving it empty on
         an Agency plan that has none changes nothing about billing. */
      body = {
        name: f.name.trim(),
        minSites: min,
        maxSites: max,
        features: featureList,
        enabled: f.enabled,
        ...(!stripeSetsPrice && !priceBlank ? { priceCents } : {}),
        ...(isAgencyCard && !priceBlank ? { priceTiers: bands.map((b) => ({ from: Number(b.from), priceCents: cents(b.price) })) } : {}),
        ...(!isEdit ? { perSite: true } : {}),
      };
    }
    try {
      if (isEdit) await backendApi.patch(`${API}/${plan!.key}`, body);
      else await backendApi.post(API, { ...body, key: f.key.trim() });
      onSaved();
    } catch (err: any) {
      setError(err.body?.error || err.message);
    } finally { setBusy(false); }
  }

  return (
    <div className="pkg-editor">
      <form className="card plan-form" onSubmit={submit}>
        <h2 className="card-title">{isEdit ? `Edit ${plan!.name}` : 'New plan'}</h2>
        {error && <div className="alert alert-error" role="alert">{error}</div>}

        {isCurrent ? (
          <Section title="Current Plan card" description="Every customer on the Free plan sees this card on My Plan. The line under the price changes it immediately.">
            <label className="field">
              <span>Tagline under the price</span>
              <input value={f.tagline} onChange={(e) => setF({ ...f, tagline: e.target.value })} />
            </label>
          </Section>
        ) : (
          <>
            <Section title="Plan">
              {!isEdit && (
                <label className="field">
                  <span>Key (permanent, e.g. studio)</span>
                  <input value={f.key} placeholder="studio" onChange={(e) => setF({ ...f, key: e.target.value })} />
                </label>
              )}
              <label className="field">
                <span>Name</span>
                <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
              </label>
            </Section>

            <Section
              title="Price"
              description={stripeSetsPrice
                ? 'This plan starts a checkout, so customers pay its Stripe price. It is not set here.'
                : isAgencyCard
                  ? 'The price customers are billed for each site, every month. The total is this price × the number of sites. Saving a price creates the matching Stripe price (test mode only); customers already subscribed keep the price they signed up on.'
                  : 'The price per site shown on the card.'}
              testId="section-price"
            >
              {!stripeSetsPrice && (
                <label className="field">
                  <span>Price per site (USD)</span>
                  <input type="text" inputMode="decimal" autoComplete="off" placeholder={isAgencyCard && !billed ? 'Not set' : '19.99'}
                    value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} />
                </label>
              )}
              {isAgencyCard && !billed && (
                <p className="muted" data-testid="price-not-set">
                  No price has been set yet, so customers pay the original Stripe price. Enter a price to bill it instead.
                </p>
              )}
              {isAgencyCard && billed && (
                <p className="muted" data-testid="price-billed">
                  Customers are billed this price. Change it and save to bill the new price.
                </p>
              )}
            </Section>

            {isAgencyCard && (
              <Section
                title="Volume discounts"
                description="Optional. From a number of sites up, every site costs less. The whole count is billed at the rate of the band it falls in, so 25 sites at '25+ → $22' costs 25 × $22. Customers see the saving on My Plan, Buy Sites and pricing."
                testId="section-discounts"
              >
                {bands.map((b, i) => {
                  const base = priceValid ? Number(priceTyped) : null;
                  const off = base && PRICE_RE.test(b.price.trim()) && Number(b.price) > 0 && Number(b.price) < base
                    ? Math.round((1 - Number(b.price) / base) * 100) : null;
                  return (
                    <div className="grid-2" key={i} data-testid="discount-row">
                      <label className="field">
                        <span>From (sites)</span>
                        <input type="text" inputMode="numeric" autoComplete="off" aria-label={`Discount ${i + 1} from sites`} placeholder="25"
                          value={b.from} onChange={(e) => setBands((cur) => cur.map((x, j) => (j === i ? { ...x, from: e.target.value } : x)))} />
                      </label>
                      <label className="field">
                        <span>Per-site price (USD){off != null ? ` — ${off}% off` : ''}</span>
                        <input type="text" inputMode="decimal" autoComplete="off" aria-label={`Discount ${i + 1} price per site`} placeholder="22"
                          value={b.price} onChange={(e) => setBands((cur) => cur.map((x, j) => (j === i ? { ...x, price: e.target.value } : x)))} />
                      </label>
                      <div>
                        <button type="button" className="btn btn-sm" aria-label={`Remove discount ${i + 1}`} onClick={() => setBands((cur) => cur.filter((_, j) => j !== i))}>×</button>
                      </div>
                    </div>
                  );
                })}
                <div>
                  <button type="button" className="btn btn-sm" disabled={bands.length >= MAX_BANDS} onClick={() => setBands((cur) => [...cur, { from: '', price: '' }])}>
                    Add volume discount
                  </button>
                </div>
                {bands.length > 0 && priceBlank && (
                  <p className="muted">Enter the base price per site above first: discounts are measured from it.</p>
                )}
              </Section>
            )}

            <Section title="Site limits" description="The fewest and most sites a customer can choose. Checkout accepts exactly this range." testId="section-sites">
              <div className="grid-2">
                <label className="field">
                  <span>Minimum sites</span>
                  <input type="text" inputMode="numeric" autoComplete="off" placeholder={String(DEFAULT_MIN_SITES)} value={f.minSites} onChange={(e) => setF({ ...f, minSites: e.target.value })} />
                </label>
                <label className="field">
                  <span>Maximum sites</span>
                  <input type="text" inputMode="numeric" autoComplete="off" placeholder={String(DEFAULT_MAX_SITES)} value={f.maxSites} onChange={(e) => setF({ ...f, maxSites: e.target.value })} />
                </label>
              </div>
            </Section>

            <Section title="Key features" description="Use {sites} and {perSite} in a feature name for the live slider values.">
              <div className="field">
                {items.map((it, i) => (
                  <div className="pkg-feature-row" key={i}>
                    <input aria-label={`Feature ${i + 1} name`} placeholder="Name" value={it.name} onChange={(e) => patchItem(i, { name: e.target.value })} />
                    <input aria-label={`Feature ${i + 1} tooltip`} placeholder="Tooltip (optional)" value={it.info ?? ''} onChange={(e) => patchItem(i, { info: e.target.value })} />
                    <input aria-label={`Feature ${i + 1} badge`} placeholder="Badge" maxLength={16} value={it.badge ?? ''} onChange={(e) => patchItem(i, { badge: e.target.value })} />
                    <label className="check">
                      <input type="checkbox" checked={!!it.comingSoon} onChange={(e) => patchItem(i, { comingSoon: e.target.checked })} /> coming soon
                    </label>
                    <button type="button" className="btn btn-sm" aria-label={`Remove feature ${i + 1}`} onClick={() => setItems((cur) => cur.filter((_, j) => j !== i))}>×</button>
                  </div>
                ))}
                <div><button type="button" className="btn btn-sm" onClick={() => setItems((cur) => [...cur, { name: '' }])}>Add feature</button></div>
              </div>
            </Section>

            <Section title="Visibility">
              <label className="switch">
                <input type="checkbox" role="switch" checked={f.enabled} onChange={(e) => setF({ ...f, enabled: e.target.checked })} />
                <span className="switch-track" />
                <span>Show this plan to customers</span>
              </label>
            </Section>
          </>
        )}

        {/* The button is disabled while the form is invalid, so it can never report why itself: say it here. */}
        {problem && !(priceBlank && !isEdit) && (<p className="field-error" role="alert" data-testid="form-problem">{problem}</p>)}
        <div className="row-actions">
          <button type="button" className="btn" onClick={onCancel}>Cancel</button>
          {' '}
          <button className="btn btn-primary" disabled={busy || !!problem} title={problem || undefined}>
            {busy ? 'Saving…' : isEdit ? 'Save changes' : 'Create plan'}
          </button>
        </div>
      </form>

      <aside className="pkg-preview-col">
        <div className="pkg-preview-title">Live preview — what customers see on My Plan</div>
        <PlanCardPreview
          name={isCurrent ? 'Current Plan' : f.name}
          description={isCurrent ? f.tagline : plan?.tagline ?? ''}
          priceCents={priceCents}
          perSite={!isCurrent && !stripeSetsPrice && (plan?.perSite ?? true)}
          minSites={min}
          maxSites={max}
          defaultSites={plan?.defaultSites ?? null}
          stripePriced={stripePriced && startsCheckout}
          chargesThisPrice={isAgencyCard && !priceBlank}
          tiers={isAgencyCard && !bandProblem ? bands.map((b) => ({ from: Number(b.from), priceCents: cents(b.price) })) : []}
          // The customer's card says /mo for every plan that starts a checkout, whatever interval is stored.
          interval={startsCheckout ? 'month' : plan?.interval ?? 'month'}
          credits={Number(f.credits || 0)}
          accent={plan?.accentColor ?? DEFAULT_ACCENT}
          ctaLabel={plan?.ctaLabel ?? ''}
          features={items}
          enabled={f.enabled}
        />
      </aside>
    </div>
  );
}
